import { PlaybackClock, SyncObject, isNewerSequence } from './syncObject';
export { SyncObject } from './syncObject';
import { emojiIndex } from './chatPolicy';
import { applyRenderPose, characterOffsets, getHatOffset, setSpriteAnchor } from './renderLayout';
import {
	Container
} from "pixi.js";
import * as PIXI from 'pixi.js';
import { game } from "../Game";
import { Settings } from "../Settings";
import { updateLeaderboard } from "./hud";
import { RippleVehicle } from "./rippleVehicle";
import {remoteGore} from './remoteGore';
import {RemoteDamage} from './remoteDamage';
import {RemoteWounds} from './woundProtocol';
import {RemoteSkinDamage} from './remoteSkinDamage';
import * as AudioManager from '../utils/AudioManager';
import { fillStrokeCircle, fillStrokeRect, setFillStyle, setStrokeStyle } from "../utils/pixiGraphics";


const DEG2RAD = 0.017453292519943296;


export const PLAYER_STATUS = {
	IDLE: 0,
	DEATH: 1,
	CHECKPOINT: 2,
}

export class RippleCharacter {
	constructor(id) {
		this.id = id;
		this.damage = new RemoteDamage();
		this.wounds = new RemoteWounds();
		this.skinDamage = new RemoteSkinDamage(this);
		this.playerIndex = -1;
		this.sprite = new Container();
		this.sprite.velocity = {x: 0, y: 0};
		this.sprite.visible = false;
		this.sprite.rippleCharacterClass = this;
		this.state = {
			body: new SyncObject(),
			head: new SyncObject(),
			shoulderLeft: new SyncObject(),
			shoulderRight: new SyncObject(),
			armLeft: new SyncObject(),
			armRight: new SyncObject(),
			handLeft: new SyncObject(),
			handRight: new SyncObject(),
			belly: new SyncObject(),
			thighLeft: new SyncObject(),
			thighRight: new SyncObject(),
			legLeft: new SyncObject(),
			legRight: new SyncObject(),
			feetLeft: new SyncObject(),
			feetRight: new SyncObject(),
			cameraObject: new SyncObject(),
		}
		this.cloud = null;
		this.chatBox = null;

		this.state.body.overflow = true;

		this.playerState = {
			name: '...',
			lobbyState: 0,
			status: 0,
			finishTime: -1
		}

		this.admin = false;
		this.vip = false;
		this.lastPackageID = -1;
		this.clock = new PlaybackClock();
		this.ping = -1;
		this.connected = true;
		this.addedToGame = false;

		this.lastStatusChange = 0;

		this.stateKeys = Object.keys(this.state);
		this.stateProcessList = [this.state.head, this.state.shoulderLeft, this.state.shoulderRight, this.state.armLeft, this.state.armRight, this.state.handLeft, this.state.handRight, this.state.belly, this.state.thighLeft, this.state.thighRight, this.state.legLeft, this.state.legRight, this.state.feetLeft, this.state.feetRight];
		this.spriteSheet = null;
		this.spriteProcessList = [];
		this.mirror = false;
		this.hatTexture = "";

		this.vehicle = new RippleVehicle(this.sprite);
	}

	async loadSkin(url){
		// Texture.from(string) only reads the cache in Pixi 8; peer skins are new blob URLs.
		const image = new Image();
		image.src = url;
		await image.decode();
		if(this.sprite.destroyed) return;

		const texture = PIXI.Texture.from(image);
		const spriteSheet = new PIXI.Spritesheet({texture, data: multiplayerAtlas});
		await spriteSheet.parse();
		if(this.sprite.destroyed){
			spriteSheet.destroy(true);
			return;
		}

		this.spriteSheet = spriteSheet;
		this.sprite.once('destroyed', () => spriteSheet.destroy(true));
		this.buildSprite();
		this.selectHat(this.hatTexture, this.hatOffsetLength, this.hatOffsetAngle);
		this.vehicle.buildSprite(spriteSheet);
	}

	buildSprite() {
		this.sprites = {
			body: new PIXI.Sprite(this.spriteSheet.textures['Normal_Core']),
			head: new PIXI.Sprite(this.spriteSheet.textures['Normal_Head_Idle']),
			shoulderLeft: new PIXI.Sprite(this.spriteSheet.textures['Normal_Shoulder']),
			shoulderRight: new PIXI.Sprite(this.spriteSheet.textures['Normal_Shoulder']),
			armLeft: new PIXI.Sprite(this.spriteSheet.textures['Normal_Arm']),
			armRight: new PIXI.Sprite(this.spriteSheet.textures['Normal_Arm']),
			handLeft: new PIXI.Sprite(this.spriteSheet.textures['Normal_Hand']),
			handRight: new PIXI.Sprite(this.spriteSheet.textures['Normal_Hand']),
			belly: new PIXI.Sprite(this.spriteSheet.textures['Normal_Belly']),
			thighLeft: new PIXI.Sprite(this.spriteSheet.textures['Normal_Thigh']),
			thighRight: new PIXI.Sprite(this.spriteSheet.textures['Normal_Thigh']),
			legLeft: new PIXI.Sprite(this.spriteSheet.textures['Normal_Leg']),
			legRight: new PIXI.Sprite(this.spriteSheet.textures['Normal_Leg']),
			feetLeft: new PIXI.Sprite(this.spriteSheet.textures['Normal_Feet']),
			feetRight: new PIXI.Sprite(this.spriteSheet.textures['Normal_Feet']),
			eyeLeft: new PIXI.Sprite(this.spriteSheet.textures['Normal_Eye']),
			eyeRight: new PIXI.Sprite(this.spriteSheet.textures['Normal_Eye']),
			mouth: new PIXI.Sprite(this.spriteSheet.textures['Mouth_Idle']),
		}


		for(let obj in this.sprites){
			setSpriteAnchor(this.sprites[obj]);
			this.sprites[obj].renderOffset = characterOffsets[obj];
		}

		this.sprite.addChild(this.sprites.handLeft);
		this.sprite.addChild(this.sprites.armLeft);
		this.sprite.addChild(this.sprites.shoulderLeft);
		this.sprite.addChild(this.sprites.thighLeft);
		this.sprite.addChild(this.sprites.legLeft);
		this.sprite.addChild(this.sprites.feetLeft);
		this.sprite.addChild(this.sprites.belly);
		this.sprite.addChild(this.sprites.body);
		this.sprite.addChild(this.sprites.head);

		this.sprites.head.addChild(this.sprites.eyeLeft);
		this.sprites.eyeLeft.x = 5;
		this.sprites.eyeLeft.y = -1;
		this.sprites.head.addChild(this.sprites.eyeRight);
		this.sprites.eyeRight.x = 27;
		this.sprites.eyeRight.y = -1;
		this.sprites.head.addChild(this.sprites.mouth);
		this.sprites.mouth.x = 14.5;
		this.sprites.mouth.y = 26.5;

		this.sprite.addChild(this.sprites.thighRight);
		this.sprite.addChild(this.sprites.legRight);
		this.sprite.addChild(this.sprites.feetRight);
		this.sprite.addChild(this.sprites.shoulderRight);
		this.sprite.addChild(this.sprites.armRight);
		this.sprite.addChild(this.sprites.handRight);

		this.buildChatBox();

		const nameText = new PIXI.Text({text: this.playerState.name, style: new PIXI.TextStyle({fontFamily:'Montserrat', fontWeight: 400, fontSize: 24, fill: 0xFFFFFF, stroke: {color: 0x000000, width: 4, join: 'round'}})})
		nameText.pivot.set(nameText.width / 2, nameText.height / 2);
		nameText.y = -140;
		this.sprite.nameText = nameText;
		this.sprite.addChild(nameText);

		this.buildCloud();

		this.spriteProcessList = [this.sprites.head, this.sprites.shoulderLeft, this.sprites.shoulderRight, this.sprites.armLeft, this.sprites.armRight, this.sprites.handLeft, this.sprites.handRight, this.sprites.belly, this.sprites.thighLeft, this.sprites.thighRight, this.sprites.legLeft, this.sprites.legRight, this.sprites.feetLeft, this.sprites.feetRight];
	}

	selectHat(texture, hatOffsetLength = 0, hatOffsetAngle = 0){
		this.hatTexture = texture;
		this.hatOffsetLength = hatOffsetLength;
		this.hatOffsetAngle = hatOffsetAngle;
		if (!this.sprites) return;

		if(this.sprites.hat){
			this.sprites.hat.destroy();
			delete this.sprites.hat;
		}

		if(this.hatTexture && this.spriteProcessList.length){
			if(['DirtBikeHelmet', 'SkateHelmet'].includes(this.hatTexture)){
				this.sprites.hat = new PIXI.Sprite(this.spriteSheet.textures[this.hatTexture]);
			} else {
				this.sprites.hat = new PIXI.Sprite(PIXI.Texture.from(this.hatTexture));
			}
			setSpriteAnchor(this.sprites.hat, ['DirtBikeHelmet', 'SkateHelmet'].includes(this.hatTexture) ? `${this.hatTexture}0000` : this.hatTexture);

			const offset = getHatOffset(hatOffsetLength, hatOffsetAngle);
			this.sprites.hat.position.set(offset.x, offset.y);

			this.sprites.head.addChild(this.sprites.hat)
		}
	}

	buildCloud(){
		this.cloud = new PIXI.Container();
		const cloudSize = 50;
		this.cloud.bg = fillStrokeCircle(new PIXI.Graphics(), 0, 0, cloudSize, 0xFFFFFF, 1, 4, 0x000000);
		this.cloud.addChild(this.cloud.bg);

		this.cloud.arrow = new PIXI.Graphics();
		const arrowSpread = 12 * DEG2RAD;
		this.cloud.addChild(this.cloud.arrow);
		this.cloud.arrow.fixArrow = () => {

			this.sprite.nameText.scale.x = this.sprite.nameText.scale.y = 1 / game.editor.cameraHolder.scale.x;

			// Pixi 8 includes the camera in the scene transform already. Convert
			// directly into the HUD's coordinates, just once, like the player sprite.
			const point = game.hudContainer.toLocal(
				new PIXI.Point(this.state.cameraObject.x, this.state.cameraObject.y),
				this.sprite.parent || game.editor.container,
			);

			this.cloud.x = point.x;
			this.cloud.y = point.y;

			const screenMargin = 60;
			let insideScreen = true;

			if(this.cloud.x < screenMargin){
				this.cloud.x = screenMargin;
				insideScreen = false;
			}
			if(this.cloud.x > window.innerWidth - screenMargin) {
				this.cloud.x = window.innerWidth - screenMargin;
				insideScreen = false;
			}

			if(this.cloud.y < screenMargin) {
				this.cloud.y = screenMargin;
				insideScreen = false;
			}
			if(this.cloud.y > window.innerHeight - screenMargin) {
				this.cloud.y = window.innerHeight - screenMargin;
				insideScreen = false;
			}

			this.cloud.arrow.clear();

			if(!insideScreen){
				this.cloud.visible = true;

				const fixedCloudSize = cloudSize - 2;
				setStrokeStyle(this.cloud.arrow, 4, 0x000000);
				setFillStyle(this.cloud.arrow, 0xFFFFFF);


				const dx = point.x - this.cloud.x;
				const dy = point.y - this.cloud.y;
				const a = Math.atan2(dy, dx);

				const arrowLength = 50;
				this.cloud.arrow.moveTo(fixedCloudSize * Math.cos(a - arrowSpread), fixedCloudSize * Math.sin(a - arrowSpread));
				this.cloud.arrow.lineTo((fixedCloudSize + arrowLength) * Math.cos(a), (fixedCloudSize + arrowLength) * Math.sin(a));
				this.cloud.arrow.lineTo(fixedCloudSize * Math.cos(a + arrowSpread), fixedCloudSize * Math.sin(a + arrowSpread));
				this.cloud.arrow.fill();
				this.cloud.arrow.stroke();

				const cameraDX = window.innerWidth / 2 - point.x;
				const cameraDY = window.innerHeight / 2 - point.y;
				const meterInPixels = 130;
				const distanceToCamera = Math.sqrt(cameraDX * cameraDX + cameraDY * cameraDY) / game.editor.cameraHolder.scale.x;
				const distanceInMeters = Math.round(distanceToCamera / meterInPixels);

				if(distanceInMeters < 10000){
					this.cloud.distanceText.text = `${distanceInMeters}m`;
				} else{
					this.cloud.distanceText.text = `9999+`;
				}

			}else{
				const minMargin = 10;
				if(Math.abs(this.state.cameraObject.x - this.state.body.x) < minMargin && Math.abs(this.state.cameraObject.y - this.state.body.y) < minMargin){
					this.cloud.visible = false;
				}else{
					this.cloud.visible = true;
				}
			}
		}

		this.cloud.head = new PIXI.Sprite(this.spriteSheet.textures['Normal_Head_Idle']);
		this.cloud.head.anchor.set(0.5, 0.5);
		this.cloud.addChild(this.cloud.head);
		this.cloud.head.scale.x = this.cloud.head.scale.y = .8;
		this.cloud.head.y = -10;

		const distanceTextStyle = {textColor:'#000', textAlign:'center', fontSize:18, fontName:'Verdana', text:'0m', wordWrap: true, wordWrapWidth: 200};
		this.cloud.distanceText = game.editor.buildTextGraphicFromObj(distanceTextStyle);
		this.cloud.distanceText.anchor.set(0.5, 0.5);
		this.cloud.distanceText.y = 30;
		this.cloud.addChild(this.cloud.distanceText);

		this.cloud.eyeLeft = new PIXI.Sprite(this.spriteSheet.textures['Normal_Eye']);
		this.cloud.eyeLeft.anchor.set(0.5, 0.5);
		this.cloud.eyeRight = new PIXI.Sprite(this.spriteSheet.textures['Normal_Eye']);
		this.cloud.eyeRight.anchor.set(0.5, 0.5);
		this.cloud.mouth = new PIXI.Sprite(this.spriteSheet.textures['Mouth_Idle']);
		this.cloud.mouth.anchor.set(0.5, 0.5);
		this.cloud.head.addChild(this.cloud.eyeLeft, this.cloud.eyeRight, this.cloud.mouth);
		this.cloud.eyeLeft.x = this.sprites.eyeLeft.x;
		this.cloud.eyeLeft.y = this.sprites.eyeLeft.y;
		this.cloud.eyeRight.x = this.sprites.eyeRight.x;
		this.cloud.eyeRight.y = this.sprites.eyeRight.y;
		this.cloud.mouth.x = this.sprites.mouth.x;
		this.cloud.mouth.y = this.sprites.mouth.y;
		this.cloud.scale.x = this.cloud.scale.y = .5;

		game.hudContainer.addChild(this.cloud);
	}

	buildChatBox(){
		this.chatBox = new PIXI.Container();

		this.chatBox.lastChatMessage = 0;
		this.chatBox.alpha = 0;
		
		this.chatBox.bg = new PIXI.Graphics();
		this.chatBox.addChild(this.chatBox.bg);

		this.chatBox.textSprite = new PIXI.Text({text: '', style: new PIXI.TextStyle({fontFamily:'Montserrat', fontWeight: 400, fontSize: 16, fill: 0x000000, wordWrap: true, wordWrapWidth: 360})})
		this.chatBox.textSprite.resolution = 2;
		this.chatBox.textSprite.anchor.set(0.5, 0.5);
		this.chatBox.addChild(this.chatBox.textSprite);
		
		this.chatBox.setText = text => {
			const textSprite = this.chatBox.textSprite;
			textSprite.text = text;

			if(this.chatBox.emoji) this.chatBox.emoji.destroy();
			delete this.chatBox.emoji;

			let targetSprite = textSprite;
			if(emojiIndex(text, Settings.numEmojis) >= 0){
				const num = emojiIndex(text, Settings.numEmojis);
				if(num >= 0){
					textSprite.text = '';
	
					const emojiTexture = PIXI.Texture.from(`emojis00${num.toString().padStart(2, '0')}`);
					this.chatBox.emoji = new PIXI.Sprite(emojiTexture);
					this.chatBox.emoji.anchor.set(0.5, 0.5);
					this.chatBox.emoji.scale.x = this.chatBox.emoji.scale.y = 0.6;
					this.chatBox.addChild(this.chatBox.emoji);
					targetSprite = this.chatBox.emoji;
				}
			}


			this.chatBox.bg.clear();
			const padding = 10;
			fillStrokeRect(this.chatBox.bg, -targetSprite.width / 2 - padding, -targetSprite.height / 2 - padding, targetSprite.width + padding * 2, targetSprite.height + padding * 2, 0xFFFFFF, 1, 4, 0x000000);

			this.chatBox.scale.x = this.chatBox.scale.y = 1 / game.editor.cameraHolder.scale.x;

			targetSprite.y = this.chatBox.bg.y = - targetSprite.height / 2 - padding;

			this.chatBox.alpha = 1;
			this.chatBox.lastChatMessage = performance.now();
		}

		this.chatBox.arrow = new PIXI.Graphics();
		this.chatBox.arrow.y = -2;
		const arrowSpread = 10;
		this.chatBox.addChild(this.chatBox.arrow);

		const arrowLength = 50;
		setStrokeStyle(this.chatBox.arrow, 4, 0x000000);
		setFillStyle(this.chatBox.arrow, 0xFFFFFF);
		this.chatBox.arrow.moveTo(-arrowSpread, 0);
		this.chatBox.arrow.lineTo(0, arrowLength);
		this.chatBox.arrow.lineTo(arrowSpread, 0);
		this.chatBox.arrow.fill();
		this.chatBox.arrow.stroke();

		this.chatBox.y = -210;

		this.sprite.addChild(this.chatBox);
	}

	processServerData(data, time){
		if(!this.sprites || data.parts.length !== this.stateProcessList.length || !data.main[0]) return;
		const resumed = this.lastPacketTime != null && time - this.lastPacketTime > 1000;
		if (!resumed && !isNewerSequence(data.id, this.lastPackageID)) return;
		const firstPacket = this.lastPackageID === -1;
		this.lastPacketTime = time;
		this.clock.receive(data.time, time);
		this.interpolationDelay = this.clock.delay;
		// Poses are placed on the sender's clock, so arrival jitter does not change their speed.
		time = data.time;

		if(this.lastPackageID === -1){
			this.sprite.visible = true;
		}

		let packedExpression = data.expression;
		const eyeBit = Math.floor(packedExpression / 100);
		packedExpression -= eyeBit * 100;
		const mouthBit = Math.floor(packedExpression / 10);

		if(eyeBit === 0){
			this.sprites.eyeLeft.texture = this.spriteSheet.textures['Normal_Eye'];
			this.sprites.eyeRight.texture = this.spriteSheet.textures['Normal_Eye'];
		}else if(eyeBit === 1){
			this.sprites.eyeLeft.texture = this.spriteSheet.textures['Normal_Eye_Closed'];
			this.sprites.eyeRight.texture = this.spriteSheet.textures['Normal_Eye_Closed'];
		}

		if(mouthBit === 0){
			this.sprites.mouth.texture = this.spriteSheet.textures['Mouth_Idle'];
		} else if(mouthBit === 1){
			this.sprites.mouth.texture = this.spriteSheet.textures['Mouth_Pain'];
		} else if(mouthBit === 2){
			this.sprites.mouth.texture = this.spriteSheet.textures['Mouth_Special'];
		}


		this.mirror = data.mirror;


		const dX = data.main[0].x - this.state.body.serverPos.x;
		const dY = data.main[0].y - this.state.body.serverPos.y;
		const d = Math.sqrt(dX * dX + dY * dY);

		const teleport = d > 600 && !this.state.body.destroyed && data.main[0].r !== 360;
		const poseEpoch = data.expression % 10;
		const reset = firstPacket || resumed || teleport || this.poseEpoch !== poseEpoch;
		this.poseEpoch = poseEpoch;
		if (reset) {
			this.suppressGore = true;
			remoteGore.removeOwner(this);
		}

		this.lastPackageID = data.id;
		this.state.body.ping = this.ping;
		if(reset){
			this.state.body.forcePosition(data.id, data.main[0].x, data.main[0].y, data.main[0].r, data.mirror, time);
			if(data.main[1]){
				this.state.cameraObject.forcePosition(data.id, data.main[1].x, data.main[1].y, data.main[1].r, data.mirror, time);
			} else{
				this.state.cameraObject.forcePosition(data.id, data.main[0].x, data.main[0].y, data.main[0].r, data.mirror, time);
			}
		} else {
			this.state.body.updateServerPosition(data.id, data.main[0].x, data.main[0].y, data.main[0].r, time, data.mirror);
			if(data.main[1]){
				this.state.cameraObject.updateServerPosition(data.id, data.main[1].x, data.main[1].y, data.main[1].r, time, data.mirror);
			} else {
				this.state.cameraObject.updateServerPosition(data.id, data.main[0].x, data.main[0].y, data.main[0].r, time, data.mirror);
			}
		}

		this.stateProcessList.forEach((state, i) => {
			const stateData = data.parts[i];

			state.ping = this.ping;
			if(reset){
				state.forcePosition(data.id, stateData.x, stateData.y, stateData.r, data.mirror, time);
			}else{
				state.updateServerPosition(data.id, stateData.x, stateData.y, stateData.r, time, data.mirror);
			}
		});

		if(data.vehicleParts.length){
			this.vehicle.processServerData(data.vehicleParts, data.id, data.mirror, time, reset);
		}

	}

	setPlayerState(state){
		this.playerState.status = state;
		this.lastStatusChange = 0;

		updateLeaderboard();
	}

	receiveWounds(packet) {
		if (this.wounds.receive(packet)) {
			this.skinDamage.clear();
			remoteGore.removeOwner(this);
		}
	}

	interpolatePosition(){
		if(!this.sprites || this.lastPackageID === -1) return;
		const time = this.clock.time(performance.now());

		this.stateKeys.forEach(key => {
			this.state[key].interpolatePosition(time, Settings.maxExtrapolation);
		});

		// apply positions
		this.sprite.velocity.x = this.state.body.x - this.sprite.x;
		this.sprite.velocity.y = this.state.body.y - this.sprite.y;

		this.sprites.body.visible = !this.state.body.destroyed;

		if(this.sprites?.body) applyRenderPose(this.sprites.body, this.state.body, characterOffsets.body, this.state.body);

		this.spriteProcessList.forEach((sprite, i) => {
			const state = this.stateProcessList[i];

			sprite.visible = !state.destroyed;

			applyRenderPose(sprite, state, sprite.renderOffset);
		});

		// The container follows the physical torso; texture offsets stay local.
		this.sprite.x = this.state.body.x;
		this.sprite.y = this.state.body.y;
		this.sprites.body.scale.x = this.state.body.mirror ? -1 : 1;
		this.skinDamage.update(performance.now(), Settings, remoteGore);

		const impact = this.damage.update(this, remoteGore, time, this.suppressGore);
		this.suppressGore = false;
		if (impact && (!this.lastGoreSound || time - this.lastGoreSound > 180)) {
			this.lastGoreSound = time;
			AudioManager.playSFX(['bash1', 'bash2', 'bash3', 'bash4'], 0.3, 0.9 + Math.random() * 0.2, {x: impact.x / Settings.PTM, y: impact.y / Settings.PTM});
		}

		// correct IK
		this.vehicle.interpolatePosition(time);

		this.cloud.arrow.fixArrow();

		if(this.playerState.status){
			this.lastStatusChange += game.editor.deltaTime;
			if(this.lastStatusChange > 3000){
				this.setPlayerState(0);
			}
		}

		// update chatbox
		if(performance.now() > this.chatBox.lastChatMessage + Settings.chatBlurTimeout && this.chatBox.alpha > 0){
			this.chatBox.alpha = this.chatBox.alpha - 0.05;
		}
	}

	clear(){
		this.skinDamage.clear();
		this.wounds.clear();
		remoteGore.removeOwner(this);
		if(this.cloud){
			this.cloud.destroy({children: true});
		}
	}
}

export const multiplayerAtlas = {"frames": {

	"Bicycle_Body":
	{
		"frame": {"x":315,"y":1,"w":187,"h":149},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":187,"h":149},
		"sourceSize": {"w":187,"h":149}
	},
	"Bicycle_Pedals":
	{
		"frame": {"x":903,"y":356,"w":34,"h":34},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":34,"h":34},
		"sourceSize": {"w":34,"h":34}
	},
	"Bicycle_WheelBack":
	{
		"frame": {"x":116,"y":219,"w":114,"h":114},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":114,"h":114},
		"sourceSize": {"w":114,"h":114}
	},
	"Bicycle_WheelBack_Bended":
	{
		"frame": {"x":231,"y":280,"w":117,"h":79},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":117,"h":79},
		"sourceSize": {"w":117,"h":79}
	},
	"Bicycle_WheelFront":
	{
		"frame": {"x":1,"y":219,"w":114,"h":114},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":114,"h":114},
		"sourceSize": {"w":114,"h":114}
	},
	"Bicycle_WheelFront_Bended":
	{
		"frame": {"x":823,"y":276,"w":117,"h":79},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":117,"h":79},
		"sourceSize": {"w":117,"h":79}
	},
	"DirtBikeHelmet":
	{
		"frame": {"x":942,"y":1,"w":79,"h":92},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":79,"h":92},
		"sourceSize": {"w":79,"h":92}
	},
	"DirtBike_Axis":
	{
		"frame": {"x":773,"y":306,"w":41,"h":74},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":41,"h":74},
		"sourceSize": {"w":41,"h":74}
	},
	"DirtBike_Body":
	{
		"frame": {"x":1,"y":1,"w":313,"h":160},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":313,"h":160},
		"sourceSize": {"w":313,"h":160}
	},
	"DirtBike_WheelBack":
	{
		"frame": {"x":444,"y":158,"w":128,"h":128},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":128,"h":128},
		"sourceSize": {"w":128,"h":128}
	},
	"DirtBike_WheelBack_Bended":
	{
		"frame": {"x":573,"y":220,"w":127,"h":97},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":127,"h":97},
		"sourceSize": {"w":127,"h":97}
	},
	"DirtBike_WheelFront":
	{
		"frame": {"x":315,"y":151,"w":128,"h":128},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":128,"h":128},
		"sourceSize": {"w":128,"h":128}
	},
	"DirtBike_WheelFront_Bended":
	{
		"frame": {"x":701,"y":220,"w":121,"h":85},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":121,"h":85},
		"sourceSize": {"w":121,"h":85}
	},
	"DirtBike_WheelSupport":
	{
		"frame": {"x":446,"y":318,"w":137,"h":49},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":137,"h":49},
		"sourceSize": {"w":137,"h":49}
	},
	"Hammer":
	{
		"frame": {"x":573,"y":164,"w":272,"h":55},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":272,"h":55},
		"sourceSize": {"w":272,"h":55}
	},
	"Hammer_2":
	{
		"frame": {"x":1,"y":162,"w":289,"h":56},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":289,"h":56},
		"sourceSize": {"w":289,"h":56}
	},
	"Mouth_Idle":
	{
		"frame": {"x":859,"y":356,"w":43,"h":31},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":43,"h":31},
		"sourceSize": {"w":43,"h":31}
	},
	"Mouth_Pain":
	{
		"frame": {"x":185,"y":334,"w":43,"h":31},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":43,"h":31},
		"sourceSize": {"w":43,"h":31}
	},
	"Mouth_Special":
	{
		"frame": {"x":815,"y":356,"w":43,"h":31},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":43,"h":31},
		"sourceSize": {"w":43,"h":31}
	},
	"Normal_Arm":
	{
		"frame": {"x":231,"y":219,"w":36,"h":58},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":36,"h":58},
		"sourceSize": {"w":36,"h":58}
	},
	"Normal_Belly":
	{
		"frame": {"x":584,"y":318,"w":84,"h":48},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":84,"h":48},
		"sourceSize": {"w":84,"h":48}
	},
	"Normal_Core":
	{
		"frame": {"x":846,"y":164,"w":122,"h":111},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":122,"h":111},
		"sourceSize": {"w":122,"h":111}
	},
	"Normal_Eye":
	{
		"frame": {"x":942,"y":131,"w":23,"h":23},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":23,"h":23},
		"sourceSize": {"w":23,"h":23}
	},
	"Normal_Eye_Closed":
	{
		"frame": {"x":268,"y":256,"w":23,"h":23},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":23,"h":23},
		"sourceSize": {"w":23,"h":23}
	},
	"Normal_Feet":
	{
		"frame": {"x":446,"y":287,"w":62,"h":22},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":62,"h":22},
		"sourceSize": {"w":62,"h":22}
	},
	"Normal_Hand":
	{
		"frame": {"x":268,"y":219,"w":40,"h":36},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":40,"h":36},
		"sourceSize": {"w":40,"h":36}
	},
	"Normal_Head_Idle":
	{
		"frame": {"x":349,"y":287,"w":96,"h":96},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":96,"h":96},
		"sourceSize": {"w":96,"h":96}
	},
	"Normal_Leg":
	{
		"frame": {"x":969,"y":183,"w":50,"h":72},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":50,"h":72},
		"sourceSize": {"w":50,"h":72}
	},
	"Normal_Shoulder":
	{
		"frame": {"x":142,"y":334,"w":42,"h":70},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":42,"h":70},
		"sourceSize": {"w":42,"h":70}
	},
	"Normal_Thigh":
	{
		"frame": {"x":969,"y":94,"w":54,"h":88},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":54,"h":88},
		"sourceSize": {"w":54,"h":88}
	},
	"Pot":
	{
		"frame": {"x":801,"y":1,"w":140,"h":162},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":140,"h":162},
		"sourceSize": {"w":140,"h":162}
	},
	"Pot_2":
	{
		"frame": {"x":660,"y":1,"w":140,"h":162},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":140,"h":162},
		"sourceSize": {"w":140,"h":162}
	},
	"SkateBoard_Board":
	{
		"frame": {"x":1,"y":334,"w":140,"h":32},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":140,"h":32},
		"sourceSize": {"w":140,"h":32}
	},
	"SkateHelmet":
	{
		"frame": {"x":941,"y":276,"w":79,"h":90},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":79,"h":90},
		"sourceSize": {"w":79,"h":90}
	},
	"Skateboard_Wheel":
	{
		"frame": {"x":823,"y":220,"w":21,"h":21},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":21,"h":21},
		"sourceSize": {"w":21,"h":21}
	},
	"YogaBall":
	{
		"frame": {"x":503,"y":1,"w":156,"h":156},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":156,"h":156},
		"sourceSize": {"w":156,"h":156}
	},
	"YogaBallHandle_Back":
	{
		"frame": {"x":291,"y":162,"w":16,"h":36},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":16,"h":36},
		"sourceSize": {"w":16,"h":36}
	},
	"YogaBall_Handle_Front":
	{
		"frame": {"x":942,"y":94,"w":18,"h":36},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":18,"h":36},
		"sourceSize": {"w":18,"h":36}
	},
	"profile":
	{
		"frame": {"x":701,"y":306,"w":71,"h":71},
		"rotated": false,
		"trimmed": false,
		"spriteSourceSize": {"x":0,"y":0,"w":71,"h":71},
		"sourceSize": {"w":71,"h":71}
	}},
	"meta": {
		"app": "Adobe Animate",
		"version": "21.0.7.42652",
		"image": "Multiplayer_Character.png",
		"format": "RGBA8888",
		"size": {"w":1024,"h":1024},
		"scale": "1"
	}
}
